import { useState } from "react";
import { X, Download, Loader2, Star, ShieldCheck } from "lucide-react";
import { exportAdminAnnoncesUrl } from "@/lib/api";
import { Field } from "./FormBuildingBlocks";

export default function ExportAnnoncesModal({ onClose, onDone }) {
  const [filters, setFilters] = useState({
    category: "",
    city: "",
    date_from: "",
    date_to: "",
    only_premium: false,
    only_verified: false,
  });
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const handleDownload = async () => {
    setBusy(true);
    try {
      const url = exportAdminAnnoncesUrl(filters);
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const disp = res.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : "fodindin-annonces.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
      onDone();
    } catch (e) {
      alert("Echec de l'export : " + (e.message || "erreur"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="export-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-lg w-full shadow-2xl"
      >
        <div className="px-6 py-4 border-b border-[var(--line)] flex items-center justify-between rounded-t-3xl">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-orange-600">Export CSV</div>
            <div className="font-bold text-lg">Telecharger les annonces</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-orange-50" data-testid="export-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-[var(--ink-soft)]">
            Filtres optionnels. Laissez vide pour exporter <b>toutes les annonces</b>.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Categorie" value={filters.category} onChange={(v) => update("category", v)} testid="export-category" />
            <Field label="Ville (contient)" value={filters.city} onChange={(v) => update("city", v)} testid="export-city" />
            <Field label="A partir du" type="date" value={filters.date_from} onChange={(v) => update("date_from", v)} testid="export-from" />
            <Field label="Jusqu'au" type="date" value={filters.date_to} onChange={(v) => update("date_to", v)} testid="export-to" />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={filters.only_premium}
                onChange={(e) => update("only_premium", e.target.checked)}
                data-testid="export-only-premium"
              />
              <Star size={14} className="text-orange-500" /> Premium uniquement
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={filters.only_verified}
                onChange={(e) => update("only_verified", e.target.checked)}
                data-testid="export-only-verified"
              />
              <ShieldCheck size={14} className="text-green-600" /> Verifiees uniquement
            </label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--line)] flex justify-end gap-2 rounded-b-3xl">
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="export-cancel">
            Annuler
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={busy}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="export-download-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {busy ? "Generation..." : "Telecharger"}
          </button>
        </div>
      </div>
    </div>
  );
}
